import { gql } from '@apollo/client'
import { Org, Permission, User } from '@mapped/schema-graph-react-apollo'
import axios from 'axios'
import jwt from 'jwt-simple'
import { NextApiRequest } from 'next'
import { clearGeoPlanCaches } from '../features/geo-plan/data/cache'
import { CoreApiClient } from '../lib/core-api'
import { beginLogout } from './sso/browser'
import { Services } from '../services'
import { ECookieName, EStorageKey, INextAPIError } from '../types/general'
import { getAllCookies, removeCookie } from '../utils/cookies'
import { getEnumKeys, getEnumValues } from '../utils/enum'
import { logger } from '../utils/logger'

export async function renewAuthToken(force?: boolean) {
  if (!force && !isAuthTokenNearToExpire(getAuthToken()!)) {
    return true
  }

  if (!getRefreshToken()) {
    return false
  }

  const { data } = await axios.post<IOAuthTokenResponse & INextAPIError>(
    `/api/refreshToken`,
    {
      refreshToken: getRefreshToken(),
    }
  )

  if (data?.error) {
    logger
      .with()
      .any('data', data?.stack)
      .logger()
      .warn('Auth token refresh failed')
    return false
  }

  setAuthToken(data?.access_token!)
  setRefreshToken(data?.refresh_token!)

  window.postMessage('token-refresh')

  return true
}

export async function renewSessionAuthToken(force?: boolean) {
  if (!force && !isAuthTokenNearToExpire(getSessionAuthToken()!)) {
    return true
  }

  if (!getSessionRefreshToken()) {
    return false
  }

  const { data } = await axios.post<IOAuthTokenResponse & INextAPIError>(
    `/api/refreshToken`,
    {
      refreshToken: getSessionRefreshToken(),
    }
  )

  if (data?.error) {
    logger
      .with()
      .any('data', data?.stack)
      .logger()
      .warn('Auth token refresh failed')
    return false
  }

  setSessionAuthToken(data?.access_token!)
  setSessionRefreshToken(data?.refresh_token!)

  window.postMessage('token-refresh')

  return true
}

export function isAuthTokenNearToExpire(t: string) {
  const token = decodeToken(t)

  if (!token || token?.error) {
    return true
  }

  const currentTime = Date.now() / 1000 // in seconds
  const margin = 600 // 10 min in seconds

  if (token.exp - margin <= currentTime) {
    return true
  }

  return false
}

export function decodeToken(token?: string) {
  try {
    const t = jwt.decode(token ?? '', '', true) ?? {}

    return {
      authToken: token,
      orgId: t['https://mapped.com/org'],
      email: t['https://mapped.com/email'],
      ...t,
    }
  } catch (error) {
    return { error }
  }
}

export async function fetchLoggedUser(req?: NextApiRequest) {
  let authToken = req ? getAuthToken(req) : getSessionAuthToken()

  try {
    const { permissions } = decodeToken(authToken!)

    const normalizedPermissions = permissions?.map((perm: string) =>
      perm.toUpperCase().replace(/\./g, '_')
    )

    const customerPermissions = getEnumValues(Permission)
    const filteredPermissions = normalizedPermissions?.filter((perm: string) =>
      customerPermissions.includes(perm)
    )

    const query = await CoreApiClient(authToken!).query<{
      user: User
      organization: Org
    }>({
      query: LOGGED_USER_QUERY,
    })

    return {
      permissions: filteredPermissions,
      authToken,
      user: query.data?.user as User,
      organization: query.data?.organization as Org,
    }
  } catch (error) {
    const { authToken: token, orgId } = decodeToken(authToken!)
    return { error, orgless: !!token && !orgId, authToken: token }
  }
}

export function clearNonMappedCookies() {
  Object.keys(getAllCookies()).forEach((cookie) => {
    if (!getEnumKeys(ECookieName).includes(cookie)) {
      removeCookie(cookie as any)
    }
  })
}

clearNonMappedCookies()

export function logout() {
  clearNonMappedCookies()

  // The geo view holds derived floor geometry in module-level memos. Two of the
  // three logout paths navigate away and would drop them anyway; the one in
  // `contexts/user.tsx` does not, so clear them here rather than rely on which
  // caller ran. Cross-tenant collision is not possible (the keys are global
  // entity ids), but holding a departed tenant's geometry in memory is not a
  // thing to leave to luck.
  clearGeoPlanCaches()

  localStorage.removeItem(EStorageKey.AUTH_TOKEN)
  localStorage.removeItem(EStorageKey.REFRESH_TOKEN)
  sessionStorage.removeItem(EStorageKey.AUTH_TOKEN)
  sessionStorage.removeItem(EStorageKey.REFRESH_TOKEN)
  localStorage.removeItem(EStorageKey.ID_TOKEN)
}

/**
 * Signs out for good: this browser's tokens dropped, then the SSO
 * orchestrator's own session ended (`beginLogout`), back to `/`.
 */
export async function signOut() {
  const idToken = getIdToken()

  logout()

  try {
    if (await beginLogout(idToken)) {
      return
    }
  } catch (e) {
    logger
      .with()
      .error(e as Error)
      .logger()
      .warn('[Auth]: the SSO logout failed, signing out of the console only')
  }

  window.location.assign('/')
}

/** The SSO orchestrator's id_token, kept only as the logout hint. */
export function getIdToken() {
  return localStorage.getItem(EStorageKey.ID_TOKEN)
}

export function setIdToken(idToken: string) {
  localStorage.setItem(EStorageKey.ID_TOKEN, idToken)
}

/**
 * Who is signed in, as an email: the session token's Mapped claim, else the
 * SSO orchestrator's id_token (a fresh sign-up's org-less token may carry
 * none). Read without verifying: only ever shown back to the person.
 */
export function signedInEmail(): string | undefined {
  for (const token of [getSessionAuthToken(), getIdToken()]) {
    const { email } = decodeToken(token ?? undefined)

    if (typeof email === 'string' && email) {
      return email
    }
  }

  return undefined
}

/**
 * Where to sign in through the SSO orchestrator's universal login, which
 * signs in and signs up alike (`src/pages/auth/login.tsx`): back to
 * `returnTo` afterwards, in `orgId` when given.
 */
export function loginUrl({
  returnTo,
  orgId,
}: { returnTo?: string; orgId?: string | null } = {}): string {
  const params = new URLSearchParams()

  if (returnTo) {
    params.set('returnTo', returnTo)
  }

  if (orgId) {
    params.set('orgId', orgId)
  }

  const query = params.toString()

  return query ? `/auth/login?${query}` : '/auth/login'
}

export function getRefreshToken() {
  return localStorage.getItem(EStorageKey.REFRESH_TOKEN)
}

export function getSessionRefreshToken() {
  return sessionStorage.getItem(EStorageKey.REFRESH_TOKEN)
}

export function setRefreshToken(refreshToken: string) {
  localStorage.setItem(EStorageKey.REFRESH_TOKEN, refreshToken)
}

export function setSessionRefreshToken(refreshToken: string) {
  sessionStorage.setItem(EStorageKey.REFRESH_TOKEN, refreshToken)
}

export function setAuthToken(authToken: string) {
  localStorage.setItem(EStorageKey.AUTH_TOKEN, authToken)
}

export function setSessionAuthToken(authToken: string) {
  sessionStorage.setItem(EStorageKey.AUTH_TOKEN, authToken)
}

export function getAuthToken(req?: NextApiRequest) {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(EStorageKey.AUTH_TOKEN)
  }

  return req?.headers?.authorization?.replace('Bearer ', '')
}

export function getSessionAuthToken() {
  let token = sessionStorage.getItem(EStorageKey.AUTH_TOKEN)
  return token || getSessionRefreshToken() ? token : getAuthToken()
}

const stc = {
  token: '',
  expiration: new Date(),
}

export async function getServiceToken() {
  try {
    const { uri, service_client_id, service_client_secret } =
      Services.token_exchange

    if (stc.expiration > new Date()) {
      return stc.token
    }

    const res = await axios.post(
      uri!,
      {
        client_id: service_client_id,
        client_secret: service_client_secret,
        grant_type: 'client_credentials',
      },

      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: btoa(service_client_id + ':' + service_client_secret),
        },
      }
    )

    stc.token = res.data.access_token
    stc.expiration = new Date(Date.now() + res.data.expires_in * 1000)

    return stc.token
  } catch (e) {
    logger
      .with()
      .error(e as Error)
      .logger()
      .error('Session token renewal failed')
    return ''
  }
}

const LOGGED_USER_QUERY = gql`
  query loggedUser {
    user: me {
      id
      name
      email
      nickname
      emailVerified
      roles
    }

    organization {
      id
      name
      stripeCustomerId
      created
    }
  }
`

export interface IOAuthTokenResponse {
  error?: string
  access_token?: string
  refresh_token?: string
  expires_in?: number
  orgId?: string
}
